import classNames from "classnames";
import { english } from "flatpickr/dist/l10n/default";
import { Vietnamese } from "flatpickr/dist/l10n/vn";
import { formValidate } from "helpers/form_helper";
import { convertDateTime, formatDateTime } from "helpers/utils";
import React, { useEffect, useMemo, useState } from "react";
import Flatpickr from "react-flatpickr";
import { useTranslation } from "react-i18next";
import { Label } from "reactstrap";
import MessageFormValidateV2 from "../MessageFormValidateV2";

/**
 * @typedef {Object} FormDateProps
 * @property {*} mainForm - The main form.
 * @property {string} fieldName - The field name.
 * @property {TranslationFunc} t - The translation function.
 * @property {boolean} [isDisabled] - Indicates if the input is disabled.
 * @property {boolean} [isRequired] - Indicates if the input is required.
 * @property {string} [title] - The title of the input.
 * @property {boolean} [isLabel] - Indicates if the input has a label.
 * @property {boolean} [isDateRange] - Indicates if the input is a date range.
 * @property {boolean} [isPlaintext] - Indicates if the input is in plaintext format.
 * @property {boolean} [isInline] - Indicates if the input is displayed inline.
 * @property {(val?: any) => void} [onChange] - Callback function for handling value changes.
 * @property {Record<string, any>} [cssStyle] - CSS styles for the input.
 * @property {string} [placeholder] - The placeholder of the input.
 * @property {boolean} [isNotFutureDate] - Indicates if the input is not a future date.
 * @property {boolean} [isNotPastDate] - Indicates if the input is not a past date.
 * @property {Date} [minDate] - The minimum date of the input.
 * @property {React.ReactNode} [elmGroupAfter] - The element to display after the input.
 * @property {boolean} [isAllowSelectHourMinute] - Indicates if the input is allowed to select hour and minute.
 */

/**
 * @param {FormDateProps} props
 */
const FormDateV2 = props => {
  const {
    mainForm,
    fieldName,
    t,
    isDisabled = false,
    isRequired = false,
    title = "",
    isLabel = true,
    isDateRange = false,
    isPlaintext = false,
    isInline = false,
    onChange = () => {},
    cssStyle = {},
    placeholder = "",
    isNotFutureDate = false,
    isNotPastDate = false,
    minDate = null,
    elmGroupAfter = null,
    isAllowSelectHourMinute = false,
  } = props;

  const { i18n } = useTranslation();

  const initOptionDateLocale = {
    altInput: true,
    altFormat: "d/m/Y",
    dateFormat: "d/m/Y",
    locale: {
      firstDayOfWeek: 1,
    },
    ...(isAllowSelectHourMinute
      ? {
          altFormat: "d/m/Y H:i",
          dateFormat: "d/m/Y H:i",
          enableTime: true,
          noCalendar: false,
          time_24hr: true,
          allowInput: false, // Disable direct input
          disableMobile: false,
        }
      : {}),
  };

  const [randomChange, setRandomChange] = useState("");

  /** @type {import('react-hook-form').UseFormReturn} */
  const {
    formState: { errors, touchedFields },
    trigger,
    setValue,
    watch,
  } = mainForm;

  const dataFieldName = watch(fieldName);

  const makeRandom = () =>
    `${(Math.random() + 1)
      .toString(36)
      .substring(6)
      .toUpperCase()}_${new Date().getTime()}-${fieldName}`;

  const customLocale = useMemo(() => {
    const lang = i18n.language;
    switch (lang) {
      case "vn":
        return {
          ...Vietnamese,
          firstDayOfWeek: initOptionDateLocale.locale.firstDayOfWeek,
        };
      default: // en
        return {
          ...english,
          firstDayOfWeek: initOptionDateLocale.locale.firstDayOfWeek,
        };
    }
  }, [i18n.language]);

  // useEffect(() => {
  //   if (typeof onChange === 'function') {
  //     onChange(dataFieldName);
  //   }
  // }, [dataFieldName]);

  /**
   * @param {object} payload
   * @param {string[]} payload.dataDateRange
   * @returns {string}
   */
  const renderDataDate = ({ dataDateRange }) => {
    let result = null;
    if (dataDateRange?.length > 0) {
      result = convertDateTime(
        dataDateRange[0],
        formatDateTime.DD_MM_YYYY
      ).toString();

      if (isDateRange) {
        result = `${result} ${String(t("common:form.to")).toLowerCase()} ${
          dataDateRange?.[1]
            ? convertDateTime(
                dataDateRange[1],
                formatDateTime.DD_MM_YYYY
              ).toString()
            : `"${t("common:form.unspecified")}"`
        }`;
      }
    }

    return result || "";
  };

  const titleLabel = title || t(`form.${fieldName}`);

  const [minDateFinal, setMinDateFinal] = useState(undefined);
  const [maxDateFinal, setMaxDateFinal] = useState(undefined);

  useEffect(() => {
    if (!randomChange) {
      setRandomChange(makeRandom());
    }
    const minDateFinal = isNotPastDate
      ? (() => {
          const d = new Date();
          d.setHours(0, 0, 0, 0);
          return d;
        })()
      : minDate
      ? minDate.toISOString()
      : undefined;
    const maxDateFinal = isNotFutureDate
      ? (() => {
          const d = new Date();
          d.setHours(23, 59, 59, 999);
          return d;
        })()
      : undefined;
    setMinDateFinal(minDateFinal);
    setMaxDateFinal(maxDateFinal);
  }, []);

  return (
    <div
      className={classNames({
        "mb-1": isLabel,
        "d-inline": isInline,
      })}
      style={{ ...cssStyle }}
      title={`${titleLabel}`}
    >
      {randomChange && (
        <>
          {isLabel && (
            <Label
              htmlFor={fieldName}
              style={{ marginBottom: isPlaintext ? "0px" : "0.5rem" }}
            >
              {titleLabel}{" "}
              {isRequired && <span className="text-danger">*</span>}
            </Label>
          )}

          {isPlaintext ? (
            <div
              className={classNames({
                "form-control-plaintext": true,
                "d-inline": isInline,
              })}
            >
              {renderDataDate({ dataDateRange: dataFieldName })}
            </div>
          ) : (
            <div
              className={classNames({
                "input-group": elmGroupAfter ? true : false,
              })}
            >
              {!isDateRange && (
                <Flatpickr
                  id={fieldName}
                  name={randomChange}
                  disabled={isDisabled}
                  className={`form-control d-block Flatpickr__Input ${formValidate(
                    errors,
                    touchedFields,
                    fieldName
                  )}`}
                  placeholder={placeholder || "DD/MM/YYYY"}
                  options={{
                    ...initOptionDateLocale,
                    altInput: undefined,
                    altFormat: undefined,
                    locale: customLocale,
                    maxDate: maxDateFinal,
                    minDate: minDateFinal,
                  }}
                  onOpen={() => {
                    trigger(fieldName);
                  }}
                  onChange={dates => {
                    setValue(fieldName, dates, {
                      shouldValidate: true,
                    });

                    if (typeof onChange === "function") {
                      onChange(dates);
                    }
                  }}
                  onValueUpdate={() => {
                    if (randomChange) {
                      /** @type {HTMLInputElement} */
                      const elm = document.querySelector(
                        `input[name='${randomChange}']`
                      );
                      if (elm) {
                        const result = renderDataDate({
                          dataDateRange: dataFieldName,
                        });
                        elm.value = result;
                      }
                    }
                  }}
                  value={dataFieldName}
                />
              )}

              {isDateRange && (
                <Flatpickr
                  id={fieldName}
                  name={randomChange}
                  disabled={isDisabled}
                  className={`form-control d-block Flatpickr__Input ${formValidate(
                    errors,
                    touchedFields,
                    fieldName
                  )}`}
                  placeholder={
                    placeholder ||
                    (isAllowSelectHourMinute
                      ? "DD/MM/YYYY HH:mm - DD/MM/YYYY HH:mm"
                      : "DD/MM/YYYY - DD/MM/YYYY")
                  }
                  options={{
                    ...initOptionDateLocale,
                    mode: "range",
                    locale: customLocale,
                    maxDate: maxDateFinal,
                    minDate: minDateFinal,
                  }}
                  onChange={dates => {
                    setValue(fieldName, dates, {
                      shouldValidate: true,
                    });

                    if (typeof onChange === "function") {
                      onChange(dates);
                    }
                  }}
                  onOpen={_selectedDates => {
                    trigger(fieldName);
                  }}
                  onClose={selectedDates => {
                    /** @type {(Date | null)[]} */
                    let data = selectedDates;
                    if (selectedDates?.length === 1) {
                      data = [selectedDates[0], null];
                    }
                    setTimeout(() => {
                      setValue(fieldName, data, {
                        shouldValidate: true,
                      });
                    });
                  }}
                  onValueUpdate={() => {
                    if (randomChange) {
                      /** @type {HTMLInputElement} */
                      const elm = document.querySelector(
                        `input[name='${randomChange}']`
                      );
                      if (elm) {
                        const result = renderDataDate({
                          dataDateRange: dataFieldName,
                        });
                        elm.value = result;
                      }
                    }
                  }}
                  value={dataFieldName}
                />
              )}
              {!isPlaintext && <>{elmGroupAfter && elmGroupAfter}</>}
            </div>
          )}

          {!isPlaintext && (
            <MessageFormValidateV2 errors={errors} fieldName={fieldName} />
          )}
        </>
      )}
    </div>
  );
};

export default FormDateV2;
